/**
 * The shell (web-plan.md F1): `.app-shell > .shell-rail(.sidebar + .account-dock) + .main-column`
 * with the geometry contract the stylesheet expects — the header floats over
 * the feed, the composer/joinbar report their measured height into
 * `--dock-h`, and `.chat-feed__scroll` pays the covers back as padding (D57: this
 * is the desktop's own skeleton, class for class).
 */
import { type JSX, Show, createMemo, createSignal, onCleanup } from "solid-js";

import type { Runtime } from "../store/runtime";
import * as mut from "../store/mutators";
import { dismissTopmost, escapable } from "../lib/escape";
import { swap } from "../lib/transition";
import { emojiVocabulary } from "../lib/wasm";
import { isDmRoom } from "../store/types";
import { AvatarCircle } from "../components/avatar";
import { Composer } from "../components/composer";
import { ActivityIndicator, Icon } from "../components/ds";
import { FeedView } from "../components/feed";
import { JoinBar } from "../components/joinbar";
import { Sidebar } from "../components/sidebar";
import { StreamView } from "../components/streamview";
import { VoiceBar } from "../components/voicebar";
import { ChannelSettingsScreen } from "./channelsettings";
import { ProfileScreen } from "./profile";
import { ReportDialog } from "../components/reportdialog";
import { ReportsScreen } from "./reports";
import { UserCard } from "./usercard";

export function Shell(props: { rt: Runtime; dark: boolean }): JSX.Element {
  const rt = props.rt;
  const s = rt.state;
  const t = rt.t;

  const [dockH, setDockH] = createSignal(64);
  // Every overlay setter is wrapped in a view transition (lib/transition.ts)
  // right here at the declaration, so the many open/close/Escape call sites
  // below stay exactly as they read — the swap is not their business. All of
  // them pass plain values, never updater functions.
  const [menu, setMenuNow] = createSignal<{ seq: number; x: number; y: number } | null>(null);
  const setMenu = (v: { seq: number; x: number; y: number } | null) =>
    swap("pop", () => setMenuNow(v));
  const [profileOpen, setProfileOpenNow] = createSignal(false);
  const setProfileOpen = (v: boolean) => swap("sheet", () => setProfileOpenNow(v));
  const [picker, setPickerNow] = createSignal<number | null>(null);
  const setPicker = (v: number | null) => swap("pop", () => setPickerNow(v));
  const [consoleOpen, setConsoleOpenNow] = createSignal(false);
  const setConsoleOpen = (v: boolean) => swap("sheet", () => setConsoleOpenNow(v));
  const [reportsOpen, setReportsOpenNow] = createSignal(false);
  const setReportsOpen = (v: boolean) => swap("sheet", () => setReportsOpenNow(v));
  const [reportSeq, setReportSeqNow] = createSignal<number | null>(null);
  const setReportSeq = (v: number | null) => swap("sheet", () => setReportSeqNow(v));

  const openRoom = createMemo(() =>
    s.selectedChannel !== null ? s.rooms.find((r) => r.channelId === s.selectedChannel) : undefined,
  );

  const dockRef = (el: HTMLElement) => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDockH(Math.round(entry.borderBoxSize[0]?.blockSize ?? el.offsetHeight));
      }
    });
    observer.observe(el);
    onCleanup(() => observer.disconnect());
  };

  const typingText = createMemo(() => {
    const ids = mut.typingUserIds(s.feed);
    if (ids.length === 0) return "";
    return t.typing(ids.map((id) => mut.nameOf(s.directory, id)));
  });

  const lastOwnSentAtMs = createMemo(() => {
    for (let i = s.feed.bubbles.length - 1; i >= 0; i -= 1) {
      const b = s.feed.bubbles[i]!;
      if (b.isOwn && b.state === "sent") return b.message.sent_at_ms;
    }
    return 0;
  });

  const editSource = createMemo(() => {
    const mode = s.feed.mode;
    if (mode.t !== "editing") return null;
    const b = s.feed.bubbles.find((x) => x.message.seq === mode.seq);
    return b ? { text: b.message.text, entities: b.message.entities ?? [] } : null;
  });

  const menuBubble = createMemo(() => {
    const m = menu();
    if (!m) return null;
    return s.feed.bubbles.find((b) => b.message.seq === m.seq) ?? null;
  });

  const [statusOpen, setStatusOpenNow] = createSignal(false);
  const setStatusOpen = (v: boolean) => swap("pop", () => setStatusOpenNow(v));

  // Under invisible the dot is grey and the word tells the truth (D187).
  const ownDotClass = () => {
    if (!s.connected) return "account-dock__dot--off";
    if (s.account.manualStatus === "dnd") return "account-dock__dot--dnd";
    if (s.account.manualStatus === "invisible") return "account-dock__dot--off";
    return s.presence.selfAfk ? "account-dock__dot--afk" : "";
  };
  const statusWord = () => {
    if (s.account.manualStatus === "dnd") return "dnd";
    if (s.account.manualStatus === "invisible") return "invisible";
    return "";
  };

  const openCall = createMemo(() => {
    const open = s.selectedChannel;
    return open !== null ? s.calls[open] : undefined;
  });

  // Everything Escape can close, declared beside nothing in particular: which
  // of them the key belongs to is the order they opened, and `lib/escape`
  // keeps that (there used to be a chain of `else if`s here, which was a
  // priority list that could not see the overlays living in other files).
  // Watching a stream is the outermost of them — Escape stops watching once
  // there is nothing left over it.
  escapable(() => menu() !== null, () => setMenu(null));
  escapable(statusOpen, () => setStatusOpen(false));
  escapable(() => picker() !== null, () => setPicker(null));
  escapable(() => reportSeq() !== null, () => setReportSeq(null));
  escapable(() => s.card.userId !== null, () => rt.closeCard());
  escapable(consoleOpen, () => setConsoleOpen(false));
  escapable(reportsOpen, () => setReportsOpen(false));
  escapable(profileOpen, () => setProfileOpen(false));
  escapable(() => Boolean(s.voice?.share), () => rt.stopWatching());

  // In-page hotkeys (F6): Ctrl+Shift+M mute, Ctrl+Shift+D deafen, Escape
  // closes the topmost layer, and Ctrl+Space held is push-to-talk while
  // muted (R43, in-page — a browser cannot see the keyboard when blurred).
  let pttHeld = false;
  const isTypingTarget = (el: EventTarget | null) =>
    el instanceof HTMLElement && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      dismissTopmost();
      return;
    }
    if (e.ctrlKey && e.shiftKey && (e.key === "M" || e.key === "m")) {
      e.preventDefault();
      rt.toggleMute();
      return;
    }
    if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
      e.preventDefault();
      rt.toggleDeafen();
      return;
    }
    if (
      e.ctrlKey &&
      e.code === "Space" &&
      !e.repeat &&
      !isTypingTarget(e.target) &&
      s.voice &&
      s.voice.muted
    ) {
      e.preventDefault();
      pttHeld = true;
      rt.toggleMute();
    }
  };
  const onKeyUp = (e: KeyboardEvent) => {
    if (pttHeld && (e.code === "Space" || e.key === "Control")) {
      pttHeld = false;
      if (s.voice && !s.voice.muted) rt.toggleMute();
    }
  };
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  onCleanup(() => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
  });

  return (
    <div class="app-shell">
      {/* The window's surface (app.css «The surface»): the pattern is a mask,
          so it needs an element that paints nothing else — masking `.app-shell`
          itself would cut the rail and the feed out with it. The tone is the
          stylesheet's default here; only the desktop has a picker for it. */}
      <div class="app-shell__pattern" />
      <div class="shell-rail">
        <Sidebar
          state={s}
          t={t}
          onOpen={(id) => rt.openChannel(id)}
          onTab={(tab) => rt.setTab(tab)}
          onCreateChannel={(name) => void rt.createChannel(name)}
          onOpenCard={(id) => rt.openCard(id)}
          onAccept={(id) => void rt.acceptRequest(id)}
          onDecline={(id) => void rt.declineRequest(id)}
        />
        <Show when={s.voice}>{(voice) => <VoiceBar rt={rt} t={t} voice={voice()} />}</Show>
        <div class="account-dock surface-pane">
          <span class="account-dock__avatar" role="button" onClick={() => setProfileOpen(true)}>
            <AvatarCircle
              name={mut.nameOf(s.directory, mut.selfIdOf(s.directory))}
              createdAt={s.account.createdAt}
              size={40}
              fileId={s.account.avatarFileId}
              absolute
            />
            <span class={`account-dock__dot ${ownDotClass()}`} />
          </span>
          <div class="account-dock__text">
            <span class="sidebar__name">{mut.nameOf(s.directory, mut.selfIdOf(s.directory))}</span>
            <span class="sidebar__status">{s.connected ? statusWord() : t.reconnecting()}</span>
          </div>
          <div class="account-dock__actions">
            <Show when={s.account.isAdmin}>
              <button
                type="button"
                class="echo-button echo-button--medium echo-button--secondary echo-button--icon-only"
                title={t.reportsTitle()}
                onClick={() => setReportsOpen(true)}
              >
                <Icon name="flag" size={15} />
              </button>
            </Show>
            <Show when={s.presence.enabled}>
              <button
                type="button"
                class="echo-button echo-button--medium echo-button--secondary echo-button--icon-only"
                onClick={() => setStatusOpen(!statusOpen())}
              >
                <Icon name="expand_less" size={15} />
              </button>
            </Show>
          </div>
        </div>
      </div>

      <Show when={statusOpen()}>
        <>
          <div class="echo-dismiss-area" onClick={() => setStatusOpen(false)} />
          <div class="echo-popover" style="left:24px;bottom:90px">
            <div class="echo-menu" role="menu">
              {(["auto", "dnd", "invisible"] as const).map((choice) => (
                <button
                  type="button"
                  class="echo-menu__item"
                  onClick={() => {
                    void rt.setManualStatus(choice);
                    setStatusOpen(false);
                  }}
                >
                  <Icon
                    name={
                      choice === "auto"
                        ? "circle"
                        : choice === "dnd"
                          ? "do_not_disturb_on"
                          : "visibility_off"
                    }
                    size={15}
                  />
                  {choice === "auto" ? "online" : choice}
                  <Show when={(s.account.manualStatus ?? "auto") === choice}>
                    <Icon name="check" size={15} />
                  </Show>
                </button>
              ))}
            </div>
          </div>
        </>
      </Show>

      <main class="main-column" style={`--dock-h:${dockH()}px`}>
        <div class="screen-header surface-pane screen-header--floating">
          <div class="screen-header__column">
            <span class="screen-header__title">{s.feed.channelName || "EchoFloat"}</span>
            <span class="screen-header__subtitle">
              <Show when={openRoom()} fallback="">
                {(room) => t.members(room().members)}
              </Show>
            </span>
          </div>
          <span class="layout-spacer" />
          <div class="screen-header__actions">
            <Show when={!s.connected}>
              <span class="offline-badge">
                <Icon name="wifi_off" size={14} />
                <span>{t.offline()}</span>
              </span>
            </Show>
            <Show when={openCall()}>
              {(call) => (
                <button
                  type="button"
                  class="call-pill call-pill--live call-pill--link"
                  title={call()
                    .participants.map((id) => mut.nameOf(s.directory, id))
                    .join(", ")}
                  onClick={() => void rt.joinVoice(call().channel_id)}
                >
                  <Icon name="call" size={14} filled />
                  <span class="call-pill__label">{t.callPill(call().participants.length)}</span>
                </button>
              )}
            </Show>
            <Show when={!openCall() && openRoom()?.member && s.voice === null}>
              <button
                type="button"
                class="echo-button echo-button--medium echo-button--secondary echo-button--icon-only"
                title={t.voiceJoin()}
                onClick={() => void rt.joinVoice(openRoom()!.channelId)}
              >
                <Icon name="call" size={15} />
              </button>
            </Show>
            <Show when={openRoom()?.admin}>
              <button
                type="button"
                class="echo-button echo-button--medium echo-button--secondary echo-button--icon-only"
                title={t.consoleTitle(openRoom()!.name)}
                onClick={() => setConsoleOpen(true)}
              >
                <Icon name="settings" size={15} />
              </button>
            </Show>
          </div>
        </div>
        <div class="main-column__body">
          <div class="chat-pane">
            <Show
              when={!s.feed.loading}
              fallback={
                <div class="chat-pane__loading">
                  <ActivityIndicator />
                </div>
              }
            >
              <Show
                when={s.feed.channelId !== 0 && (s.feed.bubbles.length > 0 || true)}
                fallback={<div class="chat-pane__empty">{t.emptyChannel()}</div>}
              >
                <FeedView
                  feed={s.feed}
                  t={t}
                  isDm={openRoom() ? isDmRoom(openRoom()!) : false}
                  selfId={mut.selfIdOf(s.directory)}
                  personOf={(id) => {
                    const p = s.directory.people[id];
                    return {
                      createdAt: p?.createdAt ?? 0,
                      avatarFileId: p?.avatarFileId ?? null,
                    };
                  }}
                  nameOf={(id) => mut.nameOf(s.directory, id)}
                  typingText={typingText()}
                  actions={{
                    toggleReaction: (seq, emoji) => void rt.toggleReaction(seq, emoji),
                    openMenu: (seq, x, y) => setMenu({ seq, x, y }),
                    loadOlder: () => void rt.loadOlder(),
                    markRead: () => rt.markRead(),
                    markReadUpTo: (seen) => rt.markReadUpTo(seen),
                    openCard: (id) => rt.openCard(id),
                  }}
                />
              </Show>
            </Show>

            <Show when={openRoom()}>
              {(room) => (
                <Show
                  when={room().member}
                  fallback={
                    <div ref={dockRef}>
                      <JoinBar
                        room={room()}
                        t={t}
                        onJoin={(password) => void rt.join(room().channelId, password)}
                      />
                    </div>
                  }
                >
                  <div ref={dockRef}>
                    <Composer
                      feed={s.feed}
                      t={t}
                      limit={Math.min(4096, room().settings.max_message_chars)}
                      slowmodeS={room().settings.slowmode_s}
                      admin={room().admin || s.account.isAdmin}
                      lastOwnSentAtMs={lastOwnSentAtMs()}
                      maxMediaBytes={room().settings.max_media_bytes}
                      onSend={(text, staged) => rt.send(text, staged)}
                      onPrepareSend={() => rt.prepareSend()}
                      onTyping={() => rt.noteTyping()}
                      onCancelMode={() => rt.cancelMode()}
                      onNotice={(text) => rt.showNotice(text)}
                      editSource={editSource()}
                    />
                  </div>
                </Show>
              )}
            </Show>
          </div>
        </div>
        <Show when={s.voice?.share}>
          {(share) => <StreamView rt={rt} t={t} share={share()} />}
        </Show>
      </main>

      <Show when={menuBubble()}>
        {(bubble) => (
          <>
            <div class="echo-dismiss-area" onClick={() => setMenu(null)} />
            <div
              class="echo-popover"
              style={`left:${Math.min(menu()!.x, window.innerWidth - 236)}px;top:${Math.min(menu()!.y, window.innerHeight - 200)}px`}
            >
              <div class="echo-menu" role="menu">
                <button
                  type="button"
                  class="echo-menu__item"
                  onClick={() => {
                    setPicker(menu()!.seq);
                    setMenu(null);
                  }}
                >
                  <Icon name="add_reaction" size={15} />
                  {t.menuReact()}
                </button>
                <button
                  type="button"
                  class="echo-menu__item"
                  onClick={() => {
                    rt.beginReply(menu()!.seq);
                    setMenu(null);
                  }}
                >
                  <Icon name="reply" size={15} />
                  {t.menuReply()}
                </button>
                <Show when={bubble().isOwn}>
                  <button
                    type="button"
                    class="echo-menu__item"
                    onClick={() => {
                      rt.beginEdit(menu()!.seq);
                      setMenu(null);
                    }}
                  >
                    <Icon name="edit" size={15} />
                    {t.menuEdit()}
                  </button>
                </Show>
                <Show when={!bubble().isOwn}>
                  <button
                    type="button"
                    class="echo-menu__item"
                    onClick={() => {
                      setReportSeq(menu()!.seq);
                      setMenu(null);
                    }}
                  >
                    <Icon name="flag" size={15} />
                    {t.menuReport()}
                  </button>
                </Show>
                <Show when={bubble().isOwn || openRoom()?.admin || s.account.isAdmin}>
                  <div class="echo-menu__separator" />
                  <button
                    type="button"
                    class="echo-menu__item echo-menu__item--destructive"
                    onClick={() => {
                      void rt.deleteMessage(menu()!.seq);
                      setMenu(null);
                    }}
                  >
                    <Icon name="delete" size={15} />
                    {t.menuDelete()}
                  </button>
                </Show>
              </div>
            </div>
          </>
        )}
      </Show>

      <Show when={picker() !== null}>
        <>
          <div class="echo-dismiss-area" onClick={() => setPicker(null)} />
          <div class="echo-popover" style="left:50%;top:30%;transform:translateX(-50%)">
            <div class="emoji-panel">
              <div class="emoji-panel__grid">
                {emojiVocabulary().map((emoji) => (
                  <button
                    type="button"
                    class="emoji-panel__glyph"
                    onClick={() => {
                      const seq = picker();
                      if (seq !== null) void rt.toggleReaction(seq, emoji);
                      setPicker(null);
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      </Show>

      <Show when={profileOpen()}>
        <ProfileScreen rt={rt} t={t} onClose={() => setProfileOpen(false)} />
      </Show>

      <Show when={consoleOpen() && openRoom()}>
        {(room) => (
          <ChannelSettingsScreen rt={rt} t={t} room={room()} onClose={() => setConsoleOpen(false)} />
        )}
      </Show>

      <Show when={reportsOpen()}>
        <ReportsScreen rt={rt} t={t} onClose={() => setReportsOpen(false)} />
      </Show>

      <Show when={reportSeq() !== null && s.feed.channelId !== 0}>
        <ReportDialog
          rt={rt}
          t={t}
          channelId={s.feed.channelId}
          seq={reportSeq()!}
          onClose={() => setReportSeq(null)}
        />
      </Show>

      <Show when={s.card.userId !== null}>
        <UserCard rt={rt} t={t} onClose={() => rt.closeCard()} />
      </Show>

      <Show when={s.ringing}>
        {(ring) => (
          <div class="incoming-call echo-material-thick echo-transition-enter">
            <span style="display:flex;flex-direction:column;gap:2px">
              <span style="font-weight:600">
                {t.ringIncoming(ring().starterName, ring().channelName)}
              </span>
            </span>
            <button
              type="button"
              class="echo-button echo-button--medium echo-button--prominent"
              onClick={() => void rt.joinVoice(ring().channelId)}
            >
              <Icon name="call" size={15} />
              {t.ringJoinHint()}
            </button>
            <button
              type="button"
              class="echo-button echo-button--medium echo-button--bordered echo-button--destructive"
              onClick={() => void rt.declineIncomingCall()}
            >
              <Icon name="call_end" size={15} />
              {t.ringDecline()}
            </button>
          </div>
        )}
      </Show>

      <Show when={s.toasts.length > 0}>
        <div style="position:absolute;right:16px;top:16px;display:flex;flex-direction:column;gap:8px;z-index:70">
          {s.toasts.map((toast) => (
            <button
              type="button"
              class="surface-pane echo-transition-enter"
              style={`display:flex;align-items:center;gap:8px;padding:10px 14px;text-align:left;cursor:pointer;border:none;max-width:280px;view-transition-name:toast-${toast.id}`}
              onClick={() => {
                if (toast.userId !== null) rt.openCard(toast.userId);
                rt.dismissToast(toast.id);
              }}
            >
              <Icon name="info" size={17} />
              {toast.text}
            </button>
          ))}
        </div>
      </Show>

      <Show when={s.notice}>
        <div class="notice-bar echo-transition-enter">
          <span>{s.notice}</span>
          <button type="button" class="notice-bar__close" onClick={() => rt.clearNotice()}>
            <Icon name="close" size={16} />
          </button>
        </div>
      </Show>
    </div>
  );
}
